/**
 * JU!CE — Carrinho: <jp-cart>.
 * Vanilla, sem libs. Progressive enhancement: sem JS, o form
 * `#jp-cart-items-form` (name="updates[<item.key>]") e os links
 * `item.url_to_remove` funcionam via POST/GET nativo (recarrega a página).
 * Com JS, +/- e Remover disparam /cart/change.js (via window.themeCore.CartApi
 * quando existir — mesma API que assets/jp-sabores.js usa) e a seção inteira
 * é re-renderizada via Section Rendering API (?section_id=), então preço,
 * economia, progresso de potes e resumo saem sempre corretos do Liquid.
 *
 * Depende de <jp-add-to-cart> (assets/jp-sabores.js) para os botões de upsell —
 * não duplicamos essa lógica aqui.
 */
(function () {
  'use strict';

  var ERROR_MS = 4000;

  function getCartApi() {
    var core = window.themeCore;
    if (core && core.CartApi && typeof core.CartApi.makeRequest === 'function') {
      return core.CartApi;
    }
    return null;
  }

  function emitCartUpdatedFallback(cart, action, params) {
    try {
      if (window.themeCore && window.themeCore.EventBus) {
        window.themeCore.cartObject = cart;
        window.themeCore.EventBus.emit(
          'cart:updated',
          Object.assign({}, cart, { action: action, params: params })
        );
        return;
      }
    } catch (e) {
      /* noop */
    }
    // Sem themeCore: atualiza o contador do header manualmente (mesmo padrão de jp-sabores.js).
    document
      .querySelectorAll('.js-header-cart-item-count-wrapper, .js-cart-count, [data-cart-count]')
      .forEach(function (el) {
        el.setAttribute('data-cart-count', cart.item_count);
        if (!el.querySelector('*')) {
          el.textContent = cart.item_count;
        }
      });
  }

  function changeCartItem(key, quantity) {
    var cartApi = getCartApi();
    if (cartApi) {
      return cartApi.makeRequest(
        cartApi.actions.CHANGE_CART_ITEM_QUANTITY,
        key,
        quantity
      );
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error((err && err.description) || 'cart_change_failed');
          });
        }
        return response.json();
      })
      .then(function (cart) {
        emitCartUpdatedFallback(cart, 'changeItem', [key, quantity]);
        return cart;
      });
  }

  function defineCart() {
    if (customElements.get('jp-cart')) return;

    class JpCartElement extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this._onClick = this.onClick.bind(this);
        this._onChange = this.onChange.bind(this);
        this._onSubmit = this.onSubmit.bind(this);
        this.addEventListener('click', this._onClick);
        this.addEventListener('change', this._onChange);
        this.addEventListener('submit', this._onSubmit);
        this.restoreCoupon();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('submit', this._onSubmit);
      }

      onClick(event) {
        var checkoutBtn = event.target.closest('[data-jp-cart-checkout]');
        if (checkoutBtn) {
          event.preventDefault();
          this.goToCheckout(checkoutBtn);
          return;
        }

        var incBtn = event.target.closest('[data-jp-cart-qty-increase]');
        var decBtn = event.target.closest('[data-jp-cart-qty-decrease]');
        var removeLink = event.target.closest('[data-jp-cart-remove]');

        if (incBtn) {
          this.stepQuantity(incBtn, 1);
          return;
        }
        if (decBtn) {
          this.stepQuantity(decBtn, -1);
          return;
        }
        if (removeLink) {
          event.preventDefault();
          var root = removeLink.closest('[data-jp-cart-item]');
          if (root) this.updateItem(root, 0);
        }
      }

      onChange(event) {
        var input = event.target.closest('[data-jp-cart-qty-input]');
        if (input) {
          var root = input.closest('[data-jp-cart-item]');
          var qty = Math.max(0, parseInt(input.value, 10) || 0);
          if (root) this.updateItem(root, qty);
        }
      }

      /* Checkout transparente da Yampi: snippets/YampiSnippet.liquid define
         window.yampiClick() (lê /cart.json, cria o carrinho na Yampi e redireciona
         para checkout_direct_url, repassando o cupom aplicável como ?promocode=).
         Cupom digitado aqui: aplica antes via /discount/CODE (grava o cookie do
         Shopify, que faz o código aparecer em /cart.json › discount_codes) e só
         então chama a Yampi. Sem yampiClick (snippet removido) cai no /checkout
         nativo com ?discount=. */
      goToCheckout(button) {
        var code = this.getCoupon();
        var self = this;

        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }

        function go() {
          if (typeof window.yampiClick === 'function') {
            window.yampiClick();
            return;
          }
          window.location.href = '/checkout' + (code ? '?discount=' + encodeURIComponent(code) : '');
        }

        if (!code) {
          go();
          return;
        }

        fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart', {
          credentials: 'same-origin',
          redirect: 'follow'
        }).then(go, go);
      }

      onSubmit(event) {
        var couponForm = event.target.closest('[data-jp-cart-coupon-form]');
        if (couponForm) {
          event.preventDefault();
          var input = couponForm.querySelector('[data-jp-cart-coupon-input]');
          this.saveCoupon(input ? input.value : '');
        }
      }

      stepQuantity(button, delta) {
        var root = button.closest('[data-jp-cart-item]');
        if (!root) return;
        var input = root.querySelector('[data-jp-cart-qty-input]');
        var current = input ? parseInt(input.value, 10) || 0 : 0;
        this.updateItem(root, Math.max(0, current + delta));
      }

      updateItem(root, quantity) {
        if (!root || this.hasAttribute('data-jp-busy')) return;
        var key = root.dataset.itemKey;
        if (!key) return;

        this.setAttribute('data-jp-busy', 'true');
        root.classList.toggle('is-removing', quantity === 0);
        this.hideError();

        var self = this;
        changeCartItem(key, quantity)
          .then(function () {
            return self.refresh();
          })
          .catch(function (err) {
            self.showError(err);
          })
          .then(function () {
            self.removeAttribute('data-jp-busy');
          });
      }

      refresh() {
        var url = window.location.pathname + window.location.search;
        var separator = url.indexOf('?') === -1 ? '?' : '&';
        return fetch(url + separator + 'section_id=' + this.sectionId)
          .then(function (response) {
            if (!response.ok) throw new Error('cart_section_render_failed');
            return response.text();
          })
          .then(this.render.bind(this));
      }

      render(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('jp-cart');
        if (!fresh) return;
        this.innerHTML = fresh.innerHTML;
        if (fresh.getAttribute('data-flavor')) {
          this.setAttribute('data-flavor', fresh.getAttribute('data-flavor'));
        }
        this.restoreCoupon();
      }

      showError(err) {
        this.setAttribute('data-state', 'error');
        var box = this.querySelector('[data-jp-cart-error]');
        if (box) box.hidden = false;
        window.setTimeout(this.hideError.bind(this), ERROR_MS);
        if (window.console && err) console.warn('[jp-cart]', err);
      }

      hideError() {
        this.removeAttribute('data-state');
        var box = this.querySelector('[data-jp-cart-error]');
        if (box) box.hidden = true;
      }

      saveCoupon(code) {
        try {
          window.localStorage.setItem('jp-cart-discount', (code || '').trim());
        } catch (e) {
          /* noop */
        }
      }

      getCoupon() {
        try {
          return window.localStorage.getItem('jp-cart-discount') || '';
        } catch (e) {
          return '';
        }
      }

      restoreCoupon() {
        var code = this.getCoupon();
        if (!code) return;
        this.querySelectorAll('[data-jp-cart-coupon-input]').forEach(function (input) {
          input.value = code;
        });
      }
    }

    customElements.define('jp-cart', JpCartElement);
  }

  defineCart();
})();
